import { use, useCallback, useEffect, useRef } from "react";
import { GetQuickRatePayload, CreateQuickTradePayload } from "@sdk/shared/types/order.types";
import { useQuickTradeFlowStore } from "@shared/src/stores/useQuickTradeFlowStore";
import { useQuickTradeRateStore } from "@shared/src/stores/useQuickTradeRateStore";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MAX_QUICK_TRADE_LIMIT, MIN_QUICK_TRADE_LIMIT } from "@shared/src/utils/constants";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { QuickTradeNotificationData } from "@sdk/index";
import { IxEventType } from "@sdk/notification/types";
import { ixToast } from "@shared/src/utils/ixToast";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";

const quickTradeFormSchema = z
  .object({
    tradeType: z.enum(["buy", "sell"]),
    amount: z
      .string()
      .min(1, "Amount is required")
      .refine(val => !isNaN(Number(val)) && Number(val) > 0, {
        message: "Amount must be greater than 0",
      })
      .refine(val => Number(val) >= MIN_QUICK_TRADE_LIMIT && Number(val) <= MAX_QUICK_TRADE_LIMIT, {
        message: `Amount must be between ${MIN_QUICK_TRADE_LIMIT} and ${MAX_QUICK_TRADE_LIMIT} `,
      }),
    paymentMethod: z.string(),
    network: z.number().min(0, "Network is required"),
    fiatCurrency: z.string().min(1, "Fiat currency is required"),
    token: z.string().min(1, "Crypto currency is required"),
    // Optional by default; conditionally required for "sell"
    paymentConfig: z
      .string()
      .optional()
      .refine(val => (val ? !isNaN(Number(val)) : true), "Payment config must be a valid number"),
  })
  .superRefine((val, ctx) => {
    // paymentConfig is required and must be a positive number when selling
    if (val.tradeType === "sell") {
      if (!val.paymentConfig) {
        ctx.addIssue({
          code: "custom",
          path: ["paymentConfig"],
          message: "Please select a payment method",
        });
      }
    }
  });

export type QuickTradeFormData = z.infer<typeof quickTradeFormSchema>;

type TradeStatus = "form" | "searching" | "merchant_found" | "incoming_order" | "expired";

export function useQuickTrade() {
  const sdk = useHostStore(state => state.sdk);
  const flowStore = useQuickTradeFlowStore();
  const currentState = useQuickTradeFlowStore(state => state.currentState);
  const getEffectiveRate = useQuickTradeRateStore(state => state.getEffectiveRate);
  const quickTradeNotification = useNotificationStore(state => state.quickTradeNotification);

  useEffect(() => {
    const handleQuickTradeEvent = async (data: QuickTradeNotificationData) => {
      const { orderHash, adId, quickTrade } = data;
      useNotificationStore.getState().setQuickTradeNotification(data);
      const status = quickTrade?.status;
      if (orderHash) {
        flowStore.goToP2PFlow(orderHash);
        return;
      }
      console.log("quick trade event", status, data);

      if (data.rejectedAdMerchant) {
        data.quickTrade && flowStore.currentState === "merchant_found" && flowStore.goToSearching(data.quickTrade);
        return ixToast.info(data.rejectedAdMerchant + " rejected your the request");
      }

      switch (status) {
        case "pending":
          if (!adId) {
            throw new Error("Missing adId");
          }
          console.log("quick trade pending", data);
          flowStore.goToMerchantFound(adId);
          break;
        case "expired":
          console.log("quick trade expired", data);
          flowStore.goToExpired();
          break;
      }
    };
    const unsubscribe = sdk?.subscribe("all", ({ data, type }) => {
      console.log("new event in quick trade", data, type);
      if (type === IxEventType.QUICK_TRADE) {
        handleQuickTradeEvent(data);
      }
    });

    return () => unsubscribe?.();
  }, [sdk, flowStore]);

  useEffect(() => {
    if (!quickTradeNotification?.quickTrade) {
      return;
    }
    if (quickTradeNotification.quickTrade?.status === "pending" && quickTradeNotification.quickTrade.currentAdId) {
      console.log("navigating to merchant found from notification");
      useQuickTradeFlowStore.getState().goToMerchantFound(quickTradeNotification.quickTrade.currentAdId);
    }
    if (quickTradeNotification.quickTrade?.status === "expired") {
      console.log("navigating to expired from notification");
      useQuickTradeFlowStore.getState().goToExpired();
    }
  }, [quickTradeNotification, currentState]);

  const defaultFormData: QuickTradeFormData = {
    tradeType: "buy",
    amount: "",
    network: sdk?.config.defaultChainId || 84532,
    fiatCurrency: sdk?.config.defaultCurrency || "GHS",
    token: sdk?.config.tokens?.find(tok => tok.networkId === sdk?.config.defaultChainId)?.id || "",
    paymentMethod: "",
  };

  const form = useForm<QuickTradeFormData>({
    resolver: zodResolver(quickTradeFormSchema),
    mode: "onChange",
    defaultValues: defaultFormData,
  });

  // Map store state to original interface
  const view = flowStore.currentState;
  const showSignInModal = flowStore.showSignInModal;
  const incomingOrder = flowStore.incomingOrder;
  const effectiveRate = getEffectiveRate();

  const quickTradeIdRef = useRef<string | null>(null);

  const submitTrade = useCallback(
    async (data: GetQuickRatePayload, rate: number, paymentConfig: string | undefined) => {
      try {
        console.log("submitting trade", data, rate);
        const walletAddress = sdk?.auth.getWalletClient()?.account?.address;
        if (!walletAddress) {
          throw new Error("Wallet not connected");
        }

        if (data.type === "sell" && !paymentConfig) {
          return ixToast.error("Please provide payment details");
        }

        const quickTradePayload: CreateQuickTradePayload = {
          trader: walletAddress,
          traderChain: data.traderChain,
          tokenAddress: data.tokenAddress,
          currency: data.currency,
          paymentMethod: data.paymentMethod,
          quantity: data.quantity,
          priceLimit: rate,
          type: data.type,
          paymentConfig: paymentConfig ? parseInt(paymentConfig) : undefined,
        };

        const response = await sdk?.orders.createQuickTrade(quickTradePayload);
        if (response?.error) throw response.error;
        if (!response?.data) throw new Error("Quick order creation failed");

        quickTradeIdRef.current = response.data.id;
        flowStore.goToSearching(quickTradePayload);
      } catch (error: any) {
        ixToast.error(error?.message || "Quick trade creation failed");
        console.error("Quick trade creation failed", error, sdk);
        flowStore.goToForm();
      }
    },
    [sdk?.auth, sdk?.orders, flowStore],
  );

  const proceedToOrder = useCallback(async () => {
    if (!flowStore.merchantAdId || !quickTradeNotification?.quickTrade) {
      const error = new Error("Missing merchant or order data");
      return;
    }
    // Logic to proceed with order, likely calling another SDK function
  }, [flowStore.merchantAdId, quickTradeNotification]);

  const cancelTrade = useCallback(async () => {
    console.log("cancelling trade");
    sdk?.orders.cancelQuickTrade(quickTradeIdRef.current || "");
    useNotificationStore.getState().setQuickTradeNotification(null);
    flowStore.goToForm();
  }, [sdk]);

  // Simplified for brevity, you would expand this
  const isAuthenticated = sdk?.auth.status === "authenticated";

  return {
    view,
    effectiveRate,
    submitTrade,
    proceedToOrder,
    isCreatingOrder: flowStore.isCreatingOrder,
    incomingOrder,
    showSignInModal,
    setShowSignInModal: flowStore.setShowSignInModal,
    isAuthenticated,
    cancelTrade,
    form,
  };
}
